"use client";

import { useState } from "react";

type FieldOption = {
    label: string;
    value: string;
};

type FieldType = "text" | "email" | "tel" | "select" | "textarea";

type ContactField = {
    name: string;
    label: string;
    type: FieldType;
    required?: boolean;
    placeholder?: string;
    options?: FieldOption[];
    autoComplete?: string;
};

type ContactFormProps = {
    fields?: ContactField[];
    submitLabel?: string;
    consentLabel?: string;
};

const defaultFields: ContactField[] = [
    {
        name: "fullName",
        label: "Full Name",
        type: "text",
        required: true,
        placeholder: "First and last name",
        autoComplete: "name"
    },
    {
        name: "email",
        label: "Email",
        type: "email",
        required: true,
        placeholder: "you@email.com",
        autoComplete: "email"
    },
    {
        name: "phone",
        label: "Phone",
        type: "tel",
        required: true,
        placeholder: "(215) 555-0123",
        autoComplete: "tel"
    },
    {
        name: "reason",
        label: "Reason for Contacting Us",
        type: "select",
        required: false,
        options: [
            { value: "general", label: "General question" },
            { value: "investing", label: "Investing" },
            { value: "property-management", label: "Property management" },
            { value: "construction", label: "Construction" },
            { value: "other", label: "Other" }
        ]
    }
];

export default function ContactForm({
    fields = defaultFields,
    submitLabel = "Send Message",
    consentLabel = "I agree to be contacted by NRD REAL ESTATE LLC via call, email, and text for real estate services. To opt out, you can reply 'stop' at any time or reply 'help' for assistance. You can also click the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary."
}: ContactFormProps) {
    const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
    const [statusMessage, setStatusMessage] = useState<string>("");

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus("submitting");
        setStatusMessage("");

        const form = event.currentTarget;
        const formData = new FormData(form);
        const payload = Object.fromEntries(formData.entries()) as Record<string, FormDataEntryValue>;
        payload.sourcePage = window.location.href;

        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setStatus("error");
                setStatusMessage(data?.error || "Something went wrong. Please try again.");
                return;
            }

            form.reset();
            setStatus("success");
            setStatusMessage("Thanks! Your message has been sent.");
        } catch (error) {
            setStatus("error");
            setStatusMessage("Unable to send right now. Please try again later.");
        }
    };

    return (
        <form onSubmit={onSubmit} className="flex w-full flex-col gap-4" aria-live="polite">
            {/* Honeypot and timestamp fields reduce automated bot submissions. */}
            <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                defaultValue=""
            />
            <input type="hidden" name="submittedAt" value={String(Date.now())} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {fields.map((field) => (
                    <div
                        key={field.name}
                        className={field.type === "textarea" ? "sm:col-span-2" : ""}
                    >
                        <label htmlFor={field.name} className="mb-2 block text-sm font-semibold text-white">
                            {field.label}
                            {field.required && <span className="text-red-400"> *</span>}
                        </label>
                        {field.type === "select" ? (
                            <select
                                id={field.name}
                                name={field.name}
                                required={field.required}
                                className="w-full rounded-md border-2 border-[var(--border-color)] bg-neutral-950 px-4 py-3 text-sm text-white focus:border-white focus:outline-none"
                                defaultValue=""
                            >
                                <option value="" disabled>
                                    Select a reason
                                </option>
                                {(field.options || []).map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ) : field.type === "textarea" ? (
                            <textarea
                                id={field.name}
                                name={field.name}
                                required={field.required}
                                rows={5}
                                placeholder={field.placeholder}
                                className="w-full rounded-md border-2 border-[var(--border-color)] bg-[var(--background2-color)] px-4 py-3 text-sm text-white focus:border-white focus:outline-none"
                            />
                        ) : (
                            <input
                                id={field.name}
                                name={field.name}
                                type={field.type}
                                required={field.required}
                                placeholder={field.placeholder}
                                autoComplete={field.autoComplete}
                                className="w-full rounded-md border-2 border-[var(--border-color)] bg-neutral-950 px-4 py-3 text-sm text-white focus:border-white focus:outline-none"
                            />
                        )}
                        {field.name === "email" && (
                            <p className="mt-2 text-xs text-gray-400">Use a valid email address (e.g., hello@nrd.realestate).</p>
                        )}
                        {field.name === "phone" && (
                            <p className="mt-2 text-xs text-gray-400">Use a valid phone number (digits only).</p>
                        )}
                    </div>
                ))}
            </div>

            <div className="flex flex-col gap-4">
                <label className="flex items-start gap-3 text-sm text-white">
                    <input
                        type="checkbox"
                        name="consent"
                        className="mt-1 h-4 w-4 rounded border-2 border-[var(--border-color)] bg-[var(--background2-color)] text-white focus:ring-2 focus:ring-white"
                    />
                    <span>
                        {consentLabel}{" "}
                        <a
                            href="/privacy-policy"
                            className="underline hover:text-gray-200"
                        >
                            Privacy Policy
                        </a>
                        {" and "}
                        <a
                            href="/toc"
                            className="underline hover:text-gray-200"
                        >
                            Terms and Conditions
                        </a>
                        .
                    </span>
                </label>
                <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="rounded-md border-2 border-white px-6 py-3 text-sm font-semibold text-white transition hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {status === "submitting" ? "Sending..." : submitLabel}
                </button>
                {status !== "idle" && (
                    <p className={status === "error" ? "text-sm text-red-400" : "text-sm text-green-400"}>
                        {statusMessage}
                    </p>
                )}
            </div>
        </form>
    );
}
